import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Edit, Trash2, Clock, Star } from 'lucide-react';
import { AdminQuizQuestion, useDeleteQuestion } from '@/hooks/useEducationAdmin';
import { ConfirmDialog } from '@/components/admin/ConfirmDialog';

interface QuestionCardProps {
  question: AdminQuizQuestion;
  index: number;
  onEdit: (question: AdminQuizQuestion) => void;
}

export const QuestionCard: React.FC<QuestionCardProps> = ({ question, index, onEdit }) => {
  const deleteQuestion = useDeleteQuestion();

  const handleDelete = async () => {
    await deleteQuestion.mutateAsync({ id: question.id, moduleId: question.module_id });
  };

  const getDifficultyColor = (level: string) => {
    switch (level) {
      case 'easy':
        return 'bg-green-500/10 text-green-700 border-green-200';
      case 'hard':
        return 'bg-red-500/10 text-red-700 border-red-200';
      default:
        return 'bg-yellow-500/10 text-yellow-700 border-yellow-200';
    }
  };

  return (
    <Card className="transition-all duration-200 hover:shadow-md">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <CardTitle className="text-base line-clamp-2">
            {index + 1}. {question.question_text}
          </CardTitle>
          <div className="flex items-center space-x-2">
            <Badge variant="outline">#{question.sort_order}</Badge>
            <Badge className={getDifficultyColor(question.difficulty_level)}>
              {question.difficulty_level}
            </Badge>
          </div>
        </div>
      </CardHeader>
      
      <CardContent className="space-y-3">
        <div className="flex items-center space-x-4 text-sm text-muted-foreground">
          <div className="flex items-center space-x-1">
            <Star className="h-3 w-3" />
            <span>{question.points} pts</span>
          </div>
          {question.time_limit_seconds && (
            <div className="flex items-center space-x-1">
              <Clock className="h-3 w-3" />
              <span>{question.time_limit_seconds}s</span>
            </div>
          )}
          <Badge variant="outline">{question.question_type}</Badge>
        </div>

        {question.options && (
          <div className="text-sm">
            <div className="text-muted-foreground mb-1">Options:</div>
            <div className="space-y-1">
              {Array.isArray(question.options) ? 
                question.options.map((option: string, idx: number) => (
                  <div 
                    key={idx} 
                    className={`px-2 py-1 rounded text-xs ${
                      option === question.correct_answer 
                        ? 'bg-green-100 text-green-800 border border-green-200' 
                        : 'bg-gray-50 text-gray-700'
                    }`}
                  >
                    {option} {option === question.correct_answer && '✓'}
                  </div>
                )) :
                Object.entries(question.options).map(([key, value]) => (
                  <div 
                    key={key} 
                    className={`px-2 py-1 rounded text-xs ${
                      key === question.correct_answer 
                        ? 'bg-green-100 text-green-800 border border-green-200' 
                        : 'bg-gray-50 text-gray-700'
                    }`}
                  >
                    {key}: {value as string} {key === question.correct_answer && '✓'}
                  </div>
                ))
              }
            </div>
          </div>
        )}

        <div className="flex items-center space-x-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onEdit(question)}
          >
            <Edit className="mr-1 h-3 w-3" />
            Edit
          </Button>
          
          <ConfirmDialog
            title="Delete Question"
            description="Are you sure you want to delete this question? This action cannot be undone."
            confirmText="Delete"
            variant="destructive"
            onConfirm={handleDelete}
            isLoading={deleteQuestion.isPending}
            trigger={
              <Button variant="outline" size="sm">
                <Trash2 className="mr-1 h-3 w-3" />
                Delete
              </Button>
            }
          />
        </div>
      </CardContent>
    </Card>
  );
};